import "./style.scss";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/src/store";
import { officeAccountingPageRequests } from "@/src/api/officeAccountingPageRequests";
import { setData, setVisibleColumns } from "@/src/store/slices/OfficeAccountingPage/officeAccountingTableSlice";
import { setFilterParams } from "@/src/store/slices/OfficeAccountingPage/officeAccountingFiltersSlice";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { useFilteredObject } from "@/src/hooks/useFilteredObject";
import { formatOfficeAccountingGeneralStatusForApi } from "@/src/constants/officeAccountingTableConfig";
import { normalizeApplicantAccountingListResponse } from "@/src/utils/officeAccountingMappers";
import { fetchApplicantUserInfo } from "@/src/utils/applicants/fetchApplicantUserInfo";
import { IOfficeAccountingTablePayload } from "@/src/types/officeAccounting";
import { IFiltersList, IRowData } from "@/src/types/applicationOnline";
import CustomFullNamePopUp from "@/src/components/CustomUtils/CustomFullNamePopUp/CustomFullNamePopUp";
import OAPHeader from "./OAPHeader/OAPHeader";
import OAPBody from "./OAPBody/OAPBody";
import OAPFooter from "./OAPFooter/OAPFooter";
import { OpenOfficeAccountingFullNameParams } from "./openOfficeAccountingFullNamePopUp";

const getSearchQuery = (filterResults: Record<string, string[] | string | null>): string => {
  const query = filterResults.query;
  return typeof query === "string" ? query.trim() : "";
};

type RawFiltersPayload = Record<string, string | null | Array<string | boolean | number>>;

const normalizeOfficeAccountingFiltersPayload = (filters: RawFiltersPayload): Record<string, unknown> => {
  const normalized: Record<string, unknown> = {};
  const addArray = (key: string, value: unknown) => {
    if (Array.isArray(value) && value.length > 0) normalized[key] = value;
  };
  const addValue = (key: string, value: unknown) => {
    if (typeof value === "string" && value.trim() !== "") normalized[key] = value.trim();
  };

  addValue("query", filters.query);
  if (Array.isArray(filters.generalStatus) && filters.generalStatus.length > 0) {
    const mappedStatus = Array.from(
      new Set(
        filters.generalStatus
          .map((value) => formatOfficeAccountingGeneralStatusForApi(String(value)) || String(value).trim())
          .filter((value) => value !== ""),
      ),
    );
    if (mappedStatus.length > 0) normalized.general_status = mappedStatus;
  }
  addArray("course", filters.course);
  addArray("color_star", filters.color_star);
  addArray("country", filters.country);

  return normalized;
};

const OAPLayout = () => {
  const dispatch = useDispatch();
  const visibleColumns = useSelector((state: RootState) => state.officeAccountingTable.visibleColumns);
  const actualOfficeTable = useSelector((state: RootState) => state.officeAccountingFilters.actualOfficeTable);
  const bodyFilters = useSelector((state: RootState) => state.officeAccountingFilters.filterResults);
  const actualFilterStatus = useSelector((state: RootState) => state.officeAccountingFilters.filterStatus);

  const [isCurrentPage, setIsCurrentPage] = useState("1");
  const [isSelectPagination, setIsSelectPagination] = useState("5");
  const [actualQuantityRows, setActualQuantityRows] = useState(0);
  const [isShowSettingsModal, setIsShowSettingsModal] = useState(false);
  const [isApPopUpFullName, setIsApPopUpFullName] = useState(false);
  const [currentDataPopUp, setCurrentDataPopUp] = useState<IRowData["userInfo"] | undefined>();
  const [currentStudentId, setCurrentStudentId] = useState<number | undefined>();
  const [currentAccountingRowId, setCurrentAccountingRowId] = useState<number | undefined>();

  const bodyFilterFetch = useFilteredObject(bodyFilters);
  const searchQuery = getSearchQuery(bodyFilterFetch as Record<string, string[] | string | null>);

  const loadActualForm = useCallback(async () => {
    dispatch(setIsPreloyder(true));
    try {
      const offset = Number(isSelectPagination) * (Number(isCurrentPage) - 1);
      const requestFilters = normalizeOfficeAccountingFiltersPayload(bodyFilterFetch as RawFiltersPayload);
      const hasBodyFilters = Object.keys(requestFilters).length > 0;
      const shouldUseFiltersApply = actualFilterStatus !== "All" || hasBodyFilters || actualOfficeTable !== "ALL";

      const [response, filterList] = await Promise.all([
        shouldUseFiltersApply
          ? officeAccountingPageRequests.applyFilters(isSelectPagination, offset, actualOfficeTable, requestFilters)
          : officeAccountingPageRequests.getApplicantAccountingList(isSelectPagination, offset, actualOfficeTable),
        officeAccountingPageRequests.getFilterList(actualOfficeTable),
      ]);

      if (response && filterList) {
        const normalized = normalizeApplicantAccountingListResponse(response);
        const tablePayload: IOfficeAccountingTablePayload = {
          data: normalized.data,
          count_form: normalized.count_form,
          filters_list: filterList,
        };
        const endpoint = shouldUseFiltersApply ? "POST /applicant-accounting/filters/apply" : "GET /applicant-accounting/list";
        console.groupCollapsed(`[OfficeAccounting] ${endpoint} (office=${actualOfficeTable})`);
        console.log("API raw response:", response);
        console.log("Filter options:", filterList);
        console.log("Mapped table rows:", normalized.data);
        console.log("Row count:", normalized.count_form);
        console.groupEnd();
        dispatch(setData(tablePayload));
        dispatch(setFilterParams(filterList));
        setActualQuantityRows(normalized.count_form);
      }
    } catch (error) {
      console.error("Office Accounting load error:", error);
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
  }, [dispatch, isSelectPagination, isCurrentPage, actualOfficeTable, bodyFilterFetch, actualFilterStatus]);

  useEffect(() => {
    loadActualForm();
  }, [loadActualForm]);

  useEffect(() => {
    if (!visibleColumns.some((col) => col.id === "id")) return;
    dispatch(setVisibleColumns(visibleColumns));
  }, [dispatch, visibleColumns]);

  useEffect(() => {
    setIsCurrentPage("1");
  }, [actualFilterStatus, actualOfficeTable, searchQuery]);

  const refreshCurrentStudentUserInfo = useCallback(async (): Promise<IRowData["userInfo"] | undefined> => {
    if (currentStudentId == null) return undefined;
    try {
      const userInfo = await fetchApplicantUserInfo(currentStudentId);
      if (userInfo) setCurrentDataPopUp(userInfo);
      return userInfo;
    } catch (error) {
      console.log("refreshCurrentStudentUserInfo error:", error);
      return undefined;
    }
  }, [currentStudentId]);

  const handleOpenFullNamePopUp = useCallback(async ({ applicantId, studentInfoId }: OpenOfficeAccountingFullNameParams) => {
    setCurrentStudentId(applicantId);
    setCurrentAccountingRowId(studentInfoId);
    try {
      const userInfo = await fetchApplicantUserInfo(applicantId);
      if (userInfo) setCurrentDataPopUp(userInfo);
    } catch (error) {
      console.log("Office Accounting full name popup error:", error);
    }
    setIsApPopUpFullName(true);
  }, []);

  return (
    <div className="container office-accounting">
      <CustomFullNamePopUp
        isApPopUpFullName={isApPopUpFullName}
        setIsApPopUpFullName={setIsApPopUpFullName}
        currentDataPopUp={currentDataPopUp}
        currentStudentId={currentStudentId}
        currentApplyInfoId={currentAccountingRowId}
        tableContext="office-accounting"
        onRefreshCurrentStudentUserInfo={refreshCurrentStudentUserInfo}
        onRefreshTableData={loadActualForm}
      />
      <OAPHeader
        loadActualForm={loadActualForm}
        isShowSettingsModal={isShowSettingsModal}
        setIsShowSettingsModal={setIsShowSettingsModal}
      />
      <OAPBody
        loadActualForm={loadActualForm}
        onOpenFullNamePopUp={handleOpenFullNamePopUp}
        isShowSettingsModal={isShowSettingsModal}
      />
      <OAPFooter
        isCurrentPage={isCurrentPage}
        setIsCurrentPage={setIsCurrentPage}
        isSelectPagination={isSelectPagination}
        setIsSelectPagination={setIsSelectPagination}
        actualQuantityRows={actualQuantityRows}
      />
    </div>
  );
};

export default OAPLayout;
